import { observable, action, computed } from 'mobx';
import { LiveClassEventType } from 'components/LiveClass/utils/consts';
import uniq from 'lodash/uniq';
import keyBy from 'lodash/keyBy';
import subMinutes from 'date-fns/sub_minutes';
import getUsersByIds from 'helper/getUsersByIds';
import getContentsByIds from 'helper/getContentsByIds';
import updateChat from 'components/LiveClass/requests/updateChat';
import getFirebase from 'helper/getFirebase';
import {
  fetchActivityMessagesFromDateSn,
  fetchActivityMessagesBefore
} from '../../requests/fetchActivityMessages';

class ActivityStore {
  @observable.ref isPopupVisible = false;
  @observable.ref loading = true;

  futureEvents = [];
  pastEvents = [];
  @observable.ref futureChatMessages = [];
  @observable.ref pastChatMessages = [];

  firstMessageDate = null;
  @observable.ref hasMore = true;
  isFetchingPrevious = false;

  constructor(store) {
    this.store = store;
  }

  @computed
  get chatMessages() {
    return [...this.pastChatMessages, ...this.futureChatMessages];
  }

  @action
  async init({ classId }) {
    this.futureChatMessages = [];
    this.pastChatMessages = [];

    this.firstMessageDate = subMinutes(new Date(), 10);
    fetchActivityMessagesFromDateSn(classId, this.firstMessageDate, this.handleActivityMessages);

    this.currentUser = await this.store.localUserMedia.user;
  }

  handleEvents = async (events) => {
    const messageEvents = events.filter(e => e.type !== LiveClassEventType.CARD_CHANGED);

    const cardIds = messageEvents.reduce((acc, e) => {
      if (e.cardId) {
        acc.push(e.cardId);
      }
      return acc;
    }, []);
    const userIds = messageEvents.map(e => e.userId);

    const uniqueCardIds = uniq(cardIds);
    const cards = await getContentsByIds(this.store.reportingLibrary, uniqueCardIds);
    const cardById = keyBy(cards, 'contentId');

    const uniqueUserIds = uniq(userIds);
    const users = await getUsersByIds(this.store.reportingLibrary, uniqueUserIds);
    const userById = keyBy(users, 'id');

    return messageEvents.map(e => ({
      ...e,
      user: userById[e.userId],
      card: cardById[e.cardId],
      timeStamp: e.updatedAt.seconds * 1000
    }));
  };

  @action
  handleActivityMessages = async (events) => {
    this.loading = false;
    this.futureEvents = events;
    const messages = await this.handleEvents(events);
    this.futureChatMessages = messages;
  };

  @action
  handleFetchMore = async () => {
    if (this.isFetchingPrevious) {
      return;
    }

    try {
      this.isFetchingPrevious = true;

      const oldestMessage = this.pastEvents.length > 0 ? this.pastEvents[0] : this.futureEvents[0];

      let oldestDate = null;
      if (oldestMessage) {
        const { firebase } = await getFirebase();
        const { seconds, nanoseconds } = oldestMessage.updatedAt;
        oldestDate = new firebase.firestore.Timestamp(seconds, nanoseconds).toDate();
      } else {
        oldestDate = this.firstMessageDate;
      }

      const { classId } = this.store.room;

      const events = await fetchActivityMessagesBefore(classId, oldestDate);

      this.hasMore = events.length > 0;
      this.pastEvents = [...events, ...this.pastEvents];

      const messages = await this.handleEvents(events);
      this.pastChatMessages = [...messages, ...this.pastChatMessages];
    } catch (error) {
      console.log('Error in fetching previous messages', error);
      window.bugsnagClient && window.bugsnagClient.notify(error);
    } finally {
      this.isFetchingPrevious = false;
    }
  };

  @action
  showPopup = () => {
    this.isPopupVisible = true;
  };

  @action
  hidePopup = () => {
    this.isPopupVisible = false;
  };

  @action
  handleSendMessage = (message) => {
    const { classId } = this.store.room;
    const { userId, companyId } = this.store;
    updateChat(classId, userId, companyId, message);
  };
}

export default ActivityStore;
